import React, { ForwardedRef } from 'react';
import clsx from 'clsx';

import styles from '@danske/sapphire-css/components/table/table.module.css';
import { DOMAttributes } from '@react-types/shared';
import { mergeProps } from 'react-aria/mergeProps';
import { type CellSpacing } from '..';
import { SapphireStyleProps, useSapphireStyleProps } from '../../utils';

export interface TableProps
  extends Omit<
      DOMAttributes<HTMLTableElement>,
      'className' | 'style' | 'dangerouslySetInnerHTML'
    >,
    SapphireStyleProps {
  /**
   * Note: Currently, we don't officially support non-table elements.
   *
   *  @default 'table'
   */
  elementType?: 'table';
  /**
   * The spacing inside table cells.
   * @default 'md'
   */
  cellSpacing?: CellSpacing;
  /**
   * Same as CSS table-layout property.
   * @default 'auto'
   */
  layout?: 'auto' | 'fixed';
  children?: React.ReactNode;
}

export function tableClassNames(props: {
  cellSpacing?: CellSpacing;
  layout?: 'auto' | 'fixed';
}): string {
  const cellSpacing =
    props.cellSpacing && props.cellSpacing !== 'md'
      ? props.cellSpacing
      : undefined;
  return clsx(
    styles['sapphire-table__table'],
    cellSpacing && styles[`sapphire-table__table--spacing-${cellSpacing}`],
    {
      [styles['sapphire-table__table--fixed-layout']]: props.layout === 'fixed',
    }
  );
}

/**
 * Corresponding to `table` element in native html. Child components may be
 * {@Link Table.Header}, {@Link Table.Body}, {@Link Table.Row}
 */

export const Table = React.forwardRef(function Table(
  { children, elementType, layout, cellSpacing, ...props }: TableProps,
  ref: ForwardedRef<HTMLTableElement>
) {
  const ElementType = elementType || 'table';
  const role = props.role || ElementType === 'table' ? undefined : 'table';

  const { styleProps, filteredProps } = useSapphireStyleProps(props);

  return (
    <ElementType
      ref={ref}
      {...mergeProps(filteredProps, { role })}
      className={clsx(
        tableClassNames({
          cellSpacing,
          layout,
        }),
        styleProps.className
      )}
      style={styleProps.style}
    >
      {children}
    </ElementType>
  );
});
